import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, AlertCircle, Loader2, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function normalizeUrl(url) {
  url = url.trim();
  if (!/^https?:\/\//i.test(url)) url = "https://" + url;
  try {
    const u = new URL(url);
    return u.origin + u.pathname.replace(/\/$/, "");
  } catch { return url; }
}

function extractDomain(url) {
  try {
    const u = new URL(normalizeUrl(url));
    return u.hostname.replace(/^www\./, "");
  } catch { return url; }
}

function isValidUrl(url) {
  try {
    const u = new URL(normalizeUrl(url));
    return u.hostname.includes(".");
  } catch { return false; }
}

export default function UrlForm({ large = false, light = false }) {
  const [url, setUrl]       = useState("");
  const [error, setError]   = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = url.trim();
    if (!trimmed) { setError("Veuillez entrer l'adresse d'un site à analyser."); return; }
    if (!isValidUrl(trimmed)) { setError("Cette adresse ne semble pas valide. Exemple : monsite.fr"); return; }
    const domain = extractDomain(trimmed);
    setLoading(true);
    navigate(`/analyse/${domain}`, { state: { url: normalizeUrl(trimmed) } });
  };

  /* On dark CTA background, use white ring style */
  const inputClass = light
    ? `w-full pl-11 pr-4 rounded-xl border-0 bg-white text-[#0E1621]
       placeholder:text-slate-400 font-normal focus:outline-none focus:ring-2
       focus:ring-white/60 transition-all ${large ? "py-4 text-base" : "py-3 text-sm"}`
    : `w-full pl-11 pr-4 rounded-xl border border-[#C8D4DF] bg-white text-[#0E1621]
       placeholder:text-slate-400 font-normal focus:outline-none focus:ring-2
       focus:ring-[#0F3D5C] focus:border-transparent transition-all
       ${large ? "py-4 text-base" : "py-3 text-sm"}`;

  const btnClass = light
    ? `flex items-center justify-center gap-2 whitespace-nowrap px-6 rounded-xl
       font-medium text-sm transition-all duration-150 shadow-sm
       bg-[#F4A623] text-white hover:bg-[#D4891A] active:bg-[#B87415]
       disabled:opacity-50 disabled:cursor-not-allowed ${large ? "py-4" : "py-3"}`
    : `btn-primary flex items-center justify-center gap-2 whitespace-nowrap
       disabled:opacity-60 disabled:cursor-not-allowed ${large ? "py-4 px-8 text-base" : "py-3 px-6 text-sm"}`;

  return (
    <form id="url-form" onSubmit={handleSubmit} className="w-full" data-testid="url-form">
      <div className={`flex flex-col sm:flex-row gap-2 ${large ? "max-w-2xl" : "max-w-xl"} mx-auto`}
           style={{ boxShadow: large && !light ? "0 4px 24px rgba(15,61,92,0.13)" : undefined,
                    borderRadius: "14px" }}>
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={url}
            onChange={e => { setUrl(e.target.value); setError(""); }}
            placeholder="Collez l'adresse du site à vérifier…"
            className={inputClass}
            style={{ fontFamily: "'DM Sans', sans-serif" }}
            disabled={loading}
            data-testid="url-input"
            aria-label="Adresse du site à analyser"
            autoComplete="off"
            spellCheck="false"
          />
        </div>
        <button
          type="submit"
          disabled={loading || !url.trim()}
          className={btnClass}
          style={{ fontFamily: "'DM Sans', sans-serif" }}
          data-testid="analyze-button"
        >
          {loading ? (
            <><Loader2 className="w-4 h-4 animate-spin" />Analyse…</>
          ) : (
            <><ArrowRight className="w-4 h-4" />Analyser</>
          )}
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 mt-3 text-red-600 text-sm max-w-2xl mx-auto" data-testid="url-error">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}
    </form>
  );
}
